import {
  useQuery,
  type UseQueryOptions,
  useSuspenseQuery,
  type UseSuspenseQueryOptions,
} from "@tanstack/react-query";

import { decompressPrefetchedData } from "../../compression";
import { useApi } from "../../context";
import type { PaginatedResponse } from "../common";
import type { CityArea } from "./definitions";

const tryInjected = (
  locale: string,
  city: number,
): PaginatedResponse<CityArea[]> | undefined => {
  const injected = (window as any)?.[`__CITY_${city}_AREA_DATA__`];
  return injected ? decompressPrefetchedData(injected, locale) : undefined;
};

type UseV1CityAreasParams = {
  city: number;
  locale: string;
};
export type UseV1CityAreasResult = PaginatedResponse<CityArea[]>;

type UseV1CityAreasOptions = UseQueryOptions<UseV1CityAreasResult>;

export const useV1CityAreas = (
  { locale, ...params }: UseV1CityAreasParams,
  options?: Partial<UseV1CityAreasOptions>,
) => {
  const url = `/api/v1/city-areas/`;
  const { cleanApi } = useApi();

  return useQuery({
    ...options,
    queryKey: [url, params, options?.enabled, locale],
    queryFn: () => {
      return (
        tryInjected(locale, params.city) ??
        cleanApi(
          {
            url,
            method: "GET",
            params,
            headers: {
              "Accept-Language": locale,
            },
          },
          {
            200: async (r) => r.data,
          },
        )
      );
    },
  });
};

type UseV1CityAreasSuspenseParams = UseV1CityAreasParams & {
  enabled?: boolean;
};
export type UseV1CityAreasSuspenseResult = PaginatedResponse<CityArea[]>;

type UseV1CityAreasSuspenseOptions =
  UseSuspenseQueryOptions<UseV1CityAreasSuspenseResult | null>;

export const useV1CityAreasSuspense = (
  { enabled, locale, ...params }: UseV1CityAreasSuspenseParams,
  options?: Partial<UseV1CityAreasSuspenseOptions>,
) => {
  const url = `/api/v1/city-areas/`;
  const { cleanApi } = useApi();

  return useSuspenseQuery({
    ...options,
    queryKey: [url, params, enabled, locale],
    queryFn: () => {
      return enabled
        ? (tryInjected(locale, params.city) ??
            cleanApi(
              {
                url,
                method: "GET",
                params,
                headers: {
                  "Accept-Language": locale,
                },
              },
              {
                200: async (r) => r.data,
              },
            ))
        : null;
    },
  });
};
